import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useRef} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {useBoardStore} from '@components/board/board-store'
import {IconButton} from '@components/common/base/buttons'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {downloadDashboardXlsx} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {cn} from '@containers/html-utils'

export interface Props {
  className?: string
  hub: Hub
  opened: boolean
  setOpened: (val: boolean) => void
}

export const ReportMenu = observer((props: Props) => {
  const {className, hub, opened, setOpened} = props
  const ref = useRef(null)

  const open = useCallback(() => setOpened(true), [setOpened])
  const close = useCallback(() => setOpened(false), [setOpened])

  const menuItems = useReportMenu(hub)

  return (
    <>
      <IconButton
        ref={ref}
        icon="IconDotsVertical"
        color="grey"
        size="small"
        wrapperSize="big"
        className={cn('relative', className)}
        forcedState={opened ? 'active' : undefined}
        onClick={open}
      />
      {opened && (
        <Dropdown
          reference={ref}
          hideArrow
          className="overflow-visible"
          placement="bottom-start"
          fallbackPlacements={['bottom-end', 'right-start', 'left-start']}
          content={<Menu onItemClicked={close} items={menuItems} />}
          visible
          onVisibleChange={close}
          theme="new-menu"
        />
      )}
    </>
  )
})

const useReportMenu = (hub: Hub): MenuItemData[] => {
  const [, boardStore] = useBoardStore()

  return useMemo(() => {
    const items: MenuItemData[] = []

    items.push({
      id: 'settings',
      icon: 'IconCustomSettings',
      title: Lang.msg.dashboardSettings,
      onClick: () => {
        boardStore.startEditReport(hub)
      }
    })

    items.push({
      id: 'download',
      icon: 'IconCustomFileDownload',
      title: Lang.msg.downloadDashboardXlsx,
      onClick: () => {
        if (hub?.data?.report) {
          downloadDashboardXlsx(hub.data.report, true)
        }
      }
    })

    return items
  }, [boardStore, hub, hub.data.report])
}
